import { JSONSchema, StorageMap } from '@ngx-pwa/local-storage';
import {
  getNotificationSound,
  INotificationSound,
} from '@principle-theorem/notifications';
import { snapshot, uid } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { duration } from 'moment-timezone';
import { noop, Observable, of, Subject, timer } from 'rxjs';
import { concatMap, switchMap, takeUntil, throttleTime } from 'rxjs/operators';

const lockSchema: JSONSchema = {
  type: 'object',
  properties: {
    key: { type: 'string' },
    expiresUnix: { type: 'number' },
  },
};

interface INotificationLockKey {
  key: string;
  expiresUnix: number;
}

export class SoundNotification {
  private _lockKey = uid();
  private _soundLockKey: string;
  private _playNotificationWithSound$ = new Subject<string>();

  constructor(
    private _lockKeySuffix: string,
    private _defaultNotificationSound$: Observable<string>,
    private _storage: StorageMap,
    private _onDestroy$: Observable<unknown>
  ) {
    this._soundLockKey = `soundLock-${this._lockKeySuffix}`;

    timer(0, duration(30, 'seconds').asMilliseconds())
      .pipe(
        switchMap(() =>
          this._storage.watch<INotificationLockKey>(
            this._soundLockKey,
            lockSchema
          )
        ),
        switchMap((currentLockHolder) =>
          this._handleLockAcquisition(currentLockHolder)
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe(noop);

    this._playNotificationWithSound$
      .pipe(
        throttleTime(2000),
        concatMap((notificationSound) => this._playSound(notificationSound)),
        takeUntil(this._onDestroy$)
      )
      .subscribe(noop);

    this._handleLockfileRelease();
  }

  static playSound(selectedSound: INotificationSound): void {
    const audio = new Audio(`/assets/sounds/${selectedSound.value}`);
    audio.volume = 0.75 * (selectedSound.volume ?? 1);
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    audio.addEventListener('canplaythrough', async () => {
      try {
        await audio.play();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error playing sound ${selectedSound?.value}`, error);
      }
    });
  }

  async triggerNotification(notificationSoundOverride?: string): Promise<void> {
    const defaultNotificationSound = await snapshot(
      this._defaultNotificationSound$
    );
    const notificationSound =
      notificationSoundOverride ?? defaultNotificationSound;

    this._playNotificationWithSound$.next(notificationSound);
  }

  private async _playSound(notificationSoundOverride: string): Promise<void> {
    const confirmLock = await this._storage
      .get<INotificationLockKey>(this._soundLockKey, lockSchema)
      .toPromise();
    if (confirmLock?.key !== this._lockKey) {
      return;
    }

    const selectedSound = getNotificationSound(notificationSoundOverride);
    if (!selectedSound) {
      // eslint-disable-next-line no-console
      console.error(
        `No sound found for notification sound override: ${notificationSoundOverride}`
      );
      return;
    }

    SoundNotification.playSound(selectedSound);
  }

  private _handleLockfileRelease(): void {
    window.addEventListener(
      'beforeunload',
      () =>
        void this._storage
          .get(this._soundLockKey)
          .pipe(
            concatMap((currentLockHolder) => {
              if (currentLockHolder !== this._lockKey) {
                return of(undefined);
              }
              return this._storage.delete(this._soundLockKey);
            })
          )
          .toPromise()
    );
  }

  private async _handleLockAcquisition(
    currentLockHolder: INotificationLockKey | undefined
  ): Promise<void> {
    const hasActiveLock = currentLockHolder?.key === this._lockKey;

    const gracePeriodExpired =
      currentLockHolder &&
      moment
        .unix(currentLockHolder.expiresUnix)
        .isBefore(moment().subtract(30, 'seconds'));

    const shouldRenewLock =
      hasActiveLock &&
      moment
        .unix(currentLockHolder.expiresUnix)
        .isBefore(moment().add(30, 'seconds'));

    const data: INotificationLockKey = {
      key: this._lockKey,
      expiresUnix: moment().add(2, 'minutes').unix(),
    };

    if (!currentLockHolder || gracePeriodExpired || shouldRenewLock) {
      await this._storage.set(this._soundLockKey, data, lockSchema).toPromise();
    }
  }
}
